/**
 * "Try it" (Tier 3 — Simulate, spec 2026-07-04 §9) — sandboxed
 * multi-turn chat runner for order workflows.
 * Route: /workflows/:workflowId/simulate
 *
 * Talks to the P4 simulate endpoints via useWorkflowSimulation (P4.T10);
 * this page owns the start screen (source + persona picker) and the
 * chat pane (banner + transcript + ChatInput). The right-hand agenda
 * rail (live agenda/side-effect projection + fire-event/timeout
 * controls) is AgendaRail (P4.T12).
 */
import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { ChatInput } from '@/components/chat/ChatInput'
import { Button } from '@/components/ui'
import { apiClient, ApiError } from '@/services/apiClient'
import { useCanAccessFeature } from '@/hooks'
import { useWorkflowConfig } from '@/hooks/useWorkflowConfig'
import { useWorkflowSimulation } from '@/hooks/useWorkflowSimulation'
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import { cn } from '@/utils'
import { AgendaRail } from './AgendaRail'

function useWorkflowDetail(workflowId: string | undefined) {
  return useQuery<WorkflowSummary, Error>({
    queryKey: ['workflows', 'detail', workflowId ?? ''],
    queryFn: () => apiClient.get<WorkflowSummary>(`/api/workflows/${workflowId}`),
    enabled: !!workflowId,
    staleTime: 60_000,
  })
}

function Guard({ children }: { children: ReactNode }) {
  return (
    <PageFrame width="form" className="py-8">
      <p className="text-sm text-muted-foreground">{children}</p>
    </PageFrame>
  )
}

/** Fix 2 (final-review, Important): surface WHY the sandbox failed to
 * start instead of leaving the Start button a silent no-op. Prefers the
 * server-provided detail (`ApiError.detail`, FastAPI's `{detail: "..."}`
 * envelope) when it's a plain string; falls back to the error's own
 * `message` (e.g. a network failure with no server response at all). */
function describeStartError(error: Error | null): string {
  if (!error) return ''
  if (error instanceof ApiError && typeof error.detail === 'string') {
    return error.detail
  }
  return error.message
}

export function WorkflowSimulatePage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const canSimulate = useCanAccessFeature('workflow_configuration', 'workflows:read')
  const detail = useWorkflowDetail(workflowId)
  const config = useWorkflowConfig(
    detail.data?.source === 'platform' && detail.data?.kind === 'order'
      ? workflowId
      : undefined,
  )
  const sim = useWorkflowSimulation({ workflowId: workflowId! })
  const [source, setSource] = useState<'published' | 'draft'>('published')
  const [personaId, setPersonaId] = useState<string | null>(null)
  const [channel, setChannel] = useState<'text' | 'voice'>('text')

  if (!canSimulate) {
    return (
      <Guard>
        You don't have permission to simulate this workflow.{' '}
        <Link className="underline" to="/workflows">Back to Workflows</Link>
      </Guard>
    )
  }
  if (detail.isLoading) {
    return <p className="py-8 text-sm text-muted-foreground">Loading workflow…</p>
  }
  if (detail.isError || !detail.data) {
    return (
      <Guard>
        Couldn't load this workflow.{' '}
        <Link className="underline" to="/workflows">Back to Workflows</Link>
      </Guard>
    )
  }
  if (detail.data.kind !== 'order') {
    return (
      <Guard>
        Simulate is only available for order workflows.{' '}
        <Link className="underline" to={`/workflows/${workflowId}`}>Back to overview</Link>
      </Guard>
    )
  }
  if (detail.data.bound_personas.length === 0) {
    return (
      <Guard>
        This workflow isn't attached to a persona yet — nothing to simulate.{' '}
        <Link className="underline" to={`/workflows/${workflowId}`}>Back to overview</Link>
      </Guard>
    )
  }

  const hasDraft = Boolean(config.data?.draft && Object.keys(config.data.draft).length > 0)
  const versionLabel = sim.session
    ? sim.session.source === 'draft'
      ? `draft of v${sim.session.base_version_number}`
      : `v${sim.session.version_number}`
    : null

  const handleStart = () => {
    sim.start.mutate({
      source,
      ...(personaId ? { persona_id: personaId } : {}),
    })
  }

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title={`Try it — ${detail.data.name}`}
        sticky
        back={{ to: `/workflows/${workflowId}`, label: 'Back to overview' }}
        subtitle={versionLabel && <p className="font-mono text-xs">{versionLabel}</p>}
        actions={
          <>
            <div className="flex rounded-sm border border-border overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setChannel('text')}
                className={cn(
                  'px-2 py-1',
                  channel === 'text'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-foreground',
                )}
              >
                Text
              </button>
              <button
                type="button"
                onClick={() => setChannel('voice')}
                className={cn(
                  'px-2 py-1',
                  channel === 'voice'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-foreground',
                )}
              >
                Voice
              </button>
            </div>
            {sim.session && (
              <Button size="sm" variant="secondary" onClick={() => sim.reset.mutate()}>
                Reset
              </Button>
            )}
          </>
        }
      />

      {sim.state === 'idle' ? (
        <div className="max-w-md mx-auto space-y-4 py-6">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">
              Source
            </legend>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="source"
                value="published"
                checked={source === 'published'}
                onChange={() => setSource('published')}
              />
              Published version
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="source"
                value="draft"
                checked={source === 'draft'}
                disabled={!hasDraft}
                onChange={() => setSource('draft')}
              />
              Your draft customization
            </label>
            {!hasDraft && (
              <p className="text-xs text-muted-foreground pl-6">
                No draft saved yet.
              </p>
            )}
          </fieldset>

          {detail.data.bound_personas.length > 1 && (
            <div>
              <label
                htmlFor="simulate-persona"
                className="block text-xs font-medium text-foreground mb-1"
              >
                Persona
              </label>
              <select
                id="simulate-persona"
                value={personaId ?? ''}
                onChange={(e) => setPersonaId(e.target.value || null)}
                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">Choose a persona</option>
                {detail.data.bound_personas.map((bp) => (
                  <option key={bp.persona_id} value={bp.persona_id}>
                    {bp.persona_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Button
            disabled={
              sim.start.isPending ||
              (detail.data.bound_personas.length > 1 && !personaId)
            }
            onClick={handleStart}
          >
            Start
          </Button>
          {sim.start.isError && (
            <p role="alert" className="text-xs text-destructive">
              Could not start the sandbox — {describeStartError(sim.start.error)}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="flex flex-col min-h-[60vh] rounded-sm border border-border">
            <div
              role="alert"
              className="m-3 rounded-sm border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-strong"
            >
              🧪 Sandbox — nothing real is sent.
            </div>
            <div className="flex-1 overflow-y-auto px-3 space-y-3">
              {sim.transcript.map((entry, i) =>
                entry.role === 'user' ? (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-muted px-3 py-2 text-sm text-foreground">
                      {entry.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-start">
                      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-primary/10 px-3 py-2 text-sm text-foreground">
                        {entry.content}
                      </div>
                    </div>
                    {entry.text_channel_sends.map((msg, j) => (
                      <div
                        key={j}
                        className="max-w-[85%] rounded-sm border border-border px-3 py-2 text-xs"
                      >
                        <p className="text-muted-foreground">
                          📱 Sent on the text channel:
                        </p>
                        <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-foreground">
                          {msg}
                        </pre>
                      </div>
                    ))}
                    {entry.error && (
                      <p className="text-xs text-destructive">{entry.error}</p>
                    )}
                  </div>
                ),
              )}
              {sim.sendMessage.isError && (
                <p role="alert" className="text-xs text-destructive">
                  Message failed — try again
                </p>
              )}
            </div>
            <ChatInput
              onSend={(m) => sim.sendMessage.mutate({ message: m, channel })}
              isLoading={sim.sendMessage.isPending}
            />
          </div>
          <div>
            <AgendaRail
              agenda={sim.agenda}
              onFireEvent={(event_name) =>
                sim.fireEvent.mutate({ mode: 'event', event_name, payload: {} })}
              onSimulateTimeout={() => sim.fireEvent.mutate({ mode: 'timeout' })}
              firing={sim.fireEvent.isPending}
            />
          </div>
        </div>
      )}
    </PageFrame>
  )
}
